import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiLayout, FiArrowRight, FiRefreshCw, FiTrendingUp, FiZap, FiCheck } = FiIcons;

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const fmtN = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n || 0);

const CompletionPrompt = ({ completedType, otherType, currentResults, onStartOther, onSkip, onRestart }) => {
  const isWebsiteDone = completedType === 'website';
  const accent = isWebsiteDone ? '#3b82f6' : '#a855f7';
  const otherAccent = isWebsiteDone ? '#a855f7' : '#3b82f6';
  const otherLabel = isWebsiteDone ? 'Branding ROI' : 'Website ROI';
  const otherIcon = isWebsiteDone ? FiLayout : FiMonitor;
  const otherDescription = isWebsiteDone
    ? 'See how strategic branding multiplies your website results with stronger donor trust and recognition.'
    : 'See how a high-converting website amplifies your brand investment with more donations and grants.';

  const synergy = (currentResults.totalAnnualBenefits || 0) * 0.25;

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-2xl w-full"
      >
        {/* Success banner */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl p-6 mb-6 text-white text-center"
          style={{ background: `linear-gradient(135deg, ${accent}, ${isWebsiteDone ? '#6366f1' : '#7c3aed'})` }}
        >
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <SafeIcon icon={FiCheck} className="text-white text-2xl" />
          </div>
          <h2 className="text-2xl font-bold mb-1">
            {isWebsiteDone ? 'Website' : 'Branding'} ROI Complete!
          </h2>
          <div className="flex justify-center gap-8 mt-4">
            <div>
              <div className="text-2xl font-bold">{fmtN(currentResults.roi)}%</div>
              <div className="text-xs opacity-70 uppercase tracking-wider">ROI</div>
            </div>
            <div className="border-x border-white/20 px-8">
              <div className="text-2xl font-bold">{fmtN(currentResults.paybackMonths)} mo</div>
              <div className="text-xs opacity-70 uppercase tracking-wider">Payback</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{fmt(currentResults.threeYearValue)}</div>
              <div className="text-xs opacity-70 uppercase tracking-wider">3-Yr Value</div>
            </div>
          </div>
        </motion.div>

        {/* Prompt card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: isWebsiteDone ? '#faf5ff' : '#eff6ff' }}>
              <SafeIcon icon={FiZap} className="text-lg" style={{ color: otherAccent }} />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Want to see the full picture?</h3>
          </div>
          <p className="text-gray-500 mb-6 text-sm leading-relaxed">
            {otherDescription}
          </p>

          {/* Synergy teaser */}
          <div className="rounded-xl p-4 mb-6 border-2"
            style={{ borderColor: isWebsiteDone ? '#e9d5ff' : '#dbeafe', background: isWebsiteDone ? '#faf5ff' : '#eff6ff' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} style={{ color: otherAccent }} />
                <span className="font-semibold text-gray-800 text-sm">Estimated synergy bonus</span>
              </div>
              <span className="font-bold text-lg" style={{ color: otherAccent }}>
                +{fmt(synergy)}/yr
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              When website & branding work together, they create 25%+ more value than either alone.
            </p>
          </div>

          {/* CTA buttons */}
          <div className="space-y-3">
            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={onStartOther}
              className="w-full flex items-center justify-between px-6 py-4 rounded-xl text-white font-bold transition-all shadow-md hover:shadow-lg"
              style={{ background: `linear-gradient(135deg, ${otherAccent}, ${isWebsiteDone ? '#7c3aed' : '#6366f1'})` }}
            >
              <div className="flex items-center gap-3">
                <SafeIcon icon={otherIcon} className="text-xl" />
                <div className="text-left">
                  <div>Calculate {otherLabel}</div>
                  <div className="text-xs opacity-75 font-normal">Takes ~2 minutes • Unlock combined report</div>
                </div>
              </div>
              <SafeIcon icon={FiArrowRight} className="text-xl" />
            </motion.button>

            <div className="flex items-center gap-3">
              <button
                onClick={onSkip}
                className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 font-medium text-sm transition-all"
              >
                Skip — Stay with my {isWebsiteDone ? 'website' : 'branding'} results
              </button>
              <button
                onClick={onRestart}
                className="flex items-center gap-1 text-gray-400 hover:text-gray-600 text-sm transition-colors px-3"
              >
                <SafeIcon icon={FiRefreshCw} className="text-xs" />
                Restart
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CompletionPrompt;